/**
 * Desktop Header
 * Container for all in app desktop header items
 * including search and profile selector
 */
import { Tooltip } from '@mui/material';
import React, { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Image from '@components/Image';
import LocationBreadcrumb from '@components/LocationBreadcrumb/';
import ProfileBubble from '@components/ProfileBubble/';

import { useSession } from '@hooks/useSession';

import vitallLogo from '@images/logos/vitall-logo.svg';

import { formatName } from '@utils/format';

import { useGetAccountSetting } from 'generated/license.generated';

import * as Style from './DesktopHeader.style';
import ProfileChangeDropdown from './ProfileChangeDropdown';
import UnreadMessageCount from './UnreadMessageCount';

const messages = defineMessages({
  loggedInProfile: {
    defaultMessage: 'Logged in as {name}',
    id: 'desktopHeader.loggedInProfile',
  },
});

const DeskopHeader: React.FC<{
  friendlyName?: string | null;
}> = ({ friendlyName }) => {
  const { formatMessage } = useIntl();
  const {
    state: { loggedInProfile, isOrg },
  } = useSession();
  const accountId = loggedInProfile?.account_id ?? '';
  const { data: accountData } = useGetAccountSetting({
    variables: {
      input: {
        account_id: accountId,
      },
    },
    skip: !accountId,
  });

  return (
    <Style.FullHeader>
      {isOrg &&
      accountId &&
      accountData?.getAccountSetting?.branding_logo_url ? (
        <Image
          alt=""
          cssStyle={{
            width: '100%',
            height: '100%',
            maxWidth: '65px',
            maxHeight: '65px',
            borderRadius: 0,
            margin: 0,
            padding: 0,
            objectFit: 'contain',
          }}
          fallback="circle-user"
          height={420}
          loading={false}
          s3_key={accountData.getAccountSetting.branding_logo_url}
          width={500}
        />
      ) : (
        <Style.Logo>
          <img alt="VITALL Logo" src={vitallLogo} />
        </Style.Logo>
      )}

      <Style.Combined>
        <Style.Left>
          <LocationBreadcrumb friendlyName={friendlyName} />
        </Style.Left>
        <Style.Right>
          <UnreadMessageCount />
          <ProfileChangeDropdown />
          <Tooltip
            arrow
            title={formatMessage(messages.loggedInProfile, {
              name: formatName(
                loggedInProfile?.title,
                loggedInProfile?.first_name,
                loggedInProfile?.last_name
              ),
            })}
          >
            <ProfileBubble s3Key={loggedInProfile?.profile_picture} size={40} />
          </Tooltip>
        </Style.Right>
      </Style.Combined>
    </Style.FullHeader>
  );
};

export default memo(DeskopHeader);
